import React from "react";
import clsx from "clsx";
import { Card } from "components/ui";
import { FolderIcon } from "@heroicons/react/24/solid";
import { PiFolderStar } from "react-icons/pi";
import { ArrowDownTrayIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { formatDate, getUploaderName, getModifierName } from "../utils/documentUtils";
import TableActionMenu, { triggerRowContextMenu } from "custom-components/shared/TableActionMenu";

const FolderCard = ({
  folder,
  onClick,
  onRename,
  onDelete,
  onSetDefault,
  onDownloadZip,
  onMove,
  onDropOnFolder,
  readOnly = false,
  displayMode = "list",
  isSelected = false,
  onToggleSelect = null,
}) => {
  const createdRaw = folder?.CreatedOn;
  const modifiedRaw = folder?.ModifiedOn;

  const safeCreatedOn = createdRaw ? formatDate(createdRaw) : "-";
  const safeModifiedOn = modifiedRaw ? formatDate(modifiedRaw) : "-";
  const uploaderName = folder?.UploadedByName || getUploaderName(folder);
  const modifierName = folder?.ModifiedByName || getModifierName(folder);

  const [isDragTarget, setIsDragTarget] = React.useState(false);
  const dragCounterRef = React.useRef(0);

  const handleDragEnter = (e) => {
    if (readOnly || !onDropOnFolder) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) {
      setIsDragTarget(true);
    }
  };

  const handleDragOver = (e) => {
    if (readOnly || !onDropOnFolder) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e) => {
    if (readOnly || !onDropOnFolder) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragTarget(false);
    }
  };

  const handleDrop = (e) => {
    if (readOnly || !onDropOnFolder) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragTarget(false);
    if (onDropOnFolder) {
      onDropOnFolder(folder, e.dataTransfer);
    }
  };

  const customActions = [];

  if (onDownloadZip) {
    customActions.push({
      key: "download-zip",
      label: "Download (.zip)",
      icon: ArrowDownTrayIcon,
      iconColor: "text-blue-500",
      onClick: (item) => onDownloadZip(item || folder),
    });
  }

  if (onMove) {
    customActions.push({
      key: "move-item",
      label: "Move To...",
      icon: ArrowRightIcon,
      iconColor: "text-indigo-500",
      onClick: (item) => onMove(item || folder),
    });
  }

  if (onSetDefault && !folder?.IsDefault) {
    customActions.push({
      key: "set-default",
      label: "Set as Default",
      icon: PiFolderStar,
      iconColor: "text-amber-500",
      onClick: (item) => onSetDefault(item || folder),
    });
  }

  if (displayMode === "grid") {
    return (
      <Card
        className={clsx(
          "p-3 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-primary-400 dark:hover:border-primary-500 border group flex flex-col justify-between min-h-[115px] w-full relative rounded-lg overflow-hidden",
          isDragTarget
            ? "ring-2 ring-amber-500 border-amber-500 bg-amber-50 dark:bg-amber-900/40 shadow-lg scale-[1.02] z-10"
            : isSelected
            ? "border-primary-500 bg-primary-50/50 dark:bg-primary-900/20 dark:border-primary-500 shadow-xs"
            : "border-gray-200/80 dark:border-dark-600 bg-white dark:bg-dark-800 shadow-2xs"
        )}
        onClick={onClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onContextMenu={(e) => {
          e.stopPropagation();
          triggerRowContextMenu(e, folder);
        }}
      >
        {isDragTarget && (
          <div className="absolute inset-0 bg-amber-500/10 dark:bg-amber-500/20 backdrop-blur-[1px] flex items-center justify-center pointer-events-none z-20">
            <span className="bg-amber-600 text-white font-bold text-xs px-2.5 py-1 rounded-full shadow-md animate-bounce">
              Drop into "{folder?.Name}"
            </span>
          </div>
        )}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            {onToggleSelect && (
              <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                <input
                  type="checkbox"
                  checked={Boolean(isSelected)}
                  onChange={() => onToggleSelect(folder)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500 cursor-pointer"
                />
              </div>
            )}
            <div className="p-1.5 rounded-md bg-primary-50 dark:bg-primary-900/20 text-amber-500 border border-primary-100/80 dark:border-primary-800/30 shrink-0 group-hover:scale-105 transition-transform">
              <FolderIcon className="h-6 w-6" />
            </div>
          </div>
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {onSetDefault && folder?.IsDefault && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200/90 dark:border-amber-500/30 shrink-0"
                title="Default Upload Folder"
              >
                <PiFolderStar className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 shrink-0" />
                Default
              </span>
            )}
            <TableActionMenu
              item={folder}
              onEdit={onRename}
              onDelete={onDelete}
              editLabel="Rename"
              deleteLabel="Delete"
              customActions={customActions}
            />
          </div>
        </div>

        <div className="min-w-0 mt-2">
          <h3
            className="text-xs font-semibold text-gray-800 dark:text-dark-100 line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors"
            title={folder?.Name}
          >
            {folder?.Name || "Untitled Folder"}
          </h3>

          <div className="mt-2 pt-2 border-t border-gray-100 dark:border-dark-700/60 space-y-0.5 text-[10px] text-gray-500 dark:text-dark-400">
            <div className="flex items-center justify-between gap-1">
              <span className="text-gray-400 dark:text-dark-400 font-normal shrink-0">Uploaded:</span>
              <span className="font-medium text-gray-600 dark:text-dark-200 text-right truncate">{safeCreatedOn}</span>
            </div>
            {uploaderName && uploaderName !== '-' && uploaderName !== 'System' && (
              <div className="flex items-center justify-between gap-1">
                <span className="text-gray-400 dark:text-dark-400 font-normal shrink-0">Uploaded By:</span>
                <span className="font-medium text-gray-600 dark:text-dark-200 text-right truncate" title={uploaderName}>
                  {uploaderName}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between gap-1">
              <span className="text-gray-400 dark:text-dark-400 font-normal shrink-0">Modified:</span>
              <span className="font-medium text-gray-600 dark:text-dark-200 text-right truncate">{safeModifiedOn}</span>
            </div>
            {modifierName && modifierName !== '-' && (
              <div className="flex items-center justify-between gap-1">
                <span className="text-gray-400 dark:text-dark-400 font-normal shrink-0">Modified By:</span>
                <span className="font-medium text-gray-600 dark:text-dark-200 text-right truncate" title={modifierName}>
                  {modifierName}
                </span>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Borderless File Explorer List View Row Style with pixel-aligned columns
  return (
    <div
      className={clsx(
        "flex items-center justify-between px-2.5 py-1.5 text-xs rounded-md cursor-pointer transition-colors group leading-tight border-b border-gray-100 dark:border-dark-700/40 last:border-0 relative",
        isDragTarget
          ? "ring-2 ring-amber-500 bg-amber-100/70 dark:bg-amber-900/50 text-amber-900 dark:text-amber-100 font-semibold scale-[1.005] z-10"
          : isSelected
          ? "bg-primary-50/70 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100"
          : "hover:bg-primary-50/50 dark:hover:bg-dark-700/60"
      )}
      onClick={onClick}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onContextMenu={(e) => {
        e.stopPropagation();
        triggerRowContextMenu(e, folder);
      }}
    >
      {isDragTarget && (
        <span className="absolute right-16 top-1/2 -translate-y-1/2 bg-amber-600 text-white font-bold text-[11px] px-2 py-0.5 rounded-md shadow-xs pointer-events-none">
          Drop into "{folder?.Name}"
        </span>
      )}
      {/* Name & Folder Icon */}
      <div className="flex items-center gap-2 min-w-[180px] flex-1 pr-3">
        {onToggleSelect && (
          <div onClick={(e) => e.stopPropagation()} className="shrink-0 flex items-center pr-1">
            <input
              type="checkbox"
              checked={Boolean(isSelected)}
              onChange={() => onToggleSelect(folder)}
              className="h-4 w-4 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500 cursor-pointer"
            />
          </div>
        )}
        <div className="shrink-0 text-amber-500 dark:text-amber-400 group-hover:scale-105 transition-transform">
          <FolderIcon className="h-4 w-4" />
        </div>

        <div className="min-w-0 flex items-center gap-1.5 flex-1">
          <span
            className="dark:text-dark-50 text-xs font-medium text-gray-800 dark:text-dark-100 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors"
            title={folder?.Name}
          >
            {folder?.Name || "Untitled Folder"}
          </span>
          {onSetDefault && folder?.IsDefault && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200/90 dark:border-amber-500/30 shrink-0 shadow-2xs">
              <PiFolderStar className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 shrink-0" />
              Default
            </span>
          )}
        </div>
      </div>

      {/* Date Uploaded Column */}
      <div className="w-40 shrink-0 pr-2 text-gray-500 dark:text-dark-300 text-xs truncate whitespace-nowrap">
        <div className="font-medium text-gray-700 dark:text-dark-200 truncate">
          {safeCreatedOn}
        </div>
        <div className="text-[10px] text-gray-400 dark:text-dark-400 truncate" title={uploaderName && uploaderName !== '-' ? uploaderName : "System"}>
          by {uploaderName && uploaderName !== '-' ? uploaderName : "System"}
        </div>
      </div>

      {/* Date Modified Column */}
      <div className="w-40 shrink-0 pr-2 text-gray-500 dark:text-dark-300 text-xs truncate whitespace-nowrap">
        <div className="font-medium text-gray-700 dark:text-dark-200 truncate">
          {safeModifiedOn}
        </div>
        {modifierName !== '-' && (
          <div className="text-[10px] text-gray-400 dark:text-dark-400 truncate" title={modifierName}>
            by {modifierName}
          </div>
        )}
      </div>

      {/* Type Column */}
      <div className="w-28 shrink-0 pr-2 text-gray-400 dark:text-dark-400 text-xs truncate whitespace-nowrap">
        File folder
      </div>

      {/* Size Column */}
      <div className="w-20 shrink-0 pr-2 text-gray-400 dark:text-dark-400 text-xs truncate whitespace-nowrap">
        -
      </div>

      {/* Actions */}
      <div
        className="flex items-center gap-0.5 shrink-0 w-12 justify-end"
        onClick={(e) => e.stopPropagation()}
      >
        <TableActionMenu
          item={folder}
          onEdit={onRename}
          onDelete={onDelete}
          editLabel="Rename"
          deleteLabel="Delete"
          customActions={customActions}
        />
      </div>
    </div>
  );
};

export default FolderCard;
